import { abgGraphqlClient, ProxyGraphClients } from '@atlassian/temp-client-utils';
import React from 'react';
import { fetchSubscriptionOrderHistory } from '../queries/subscription-order-history';
import {
  OrderItemRequestType,
  EntitlementDetails,
  Query,
  BillingFrequency,
  OrderTransitionType,
} from '@css-xp/graphtypes/src/generated-types-new';

export const useSubscriptionHelper = () => {
  const client = React.useMemo(() => abgGraphqlClient(ProxyGraphClients.ANS), []);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);

  const calculateSubscriptionStartDate = React.useCallback(
    (entitlementDetails: EntitlementDetails, billingStartDate?: string): string | null => {
      const orders = entitlementDetails.orders?.nodes;
      const amendmentOrders =
        orders?.filter((order) => {
          if (!order) return false;
          const type = order.orderLineItems?.nodes[0]?.type;
          const orderEntitlementId = order.orderLineItems?.nodes[0]?.entitlementId;
          const orderTransitionType = order.orderLineItems?.nodes[0]?.orderTransitionType;

          //checking if invoice date is before order date. If it is there then not considering that order
          const isInvoiceDateBeforeOrder =
            billingStartDate && order?.created ? new Date(billingStartDate) < new Date(order.created) : true;
          //checking invoice date before order or if transition type is not IMMEDIATE then returning false and not considering that order;
          if (isInvoiceDateBeforeOrder || orderTransitionType !== OrderTransitionType.IMMEDIATE) return false;

          const isValidOrderType =
            type &&
            [
              OrderItemRequestType.AMENDMENT_ORDER,
              OrderItemRequestType.REACTIVATION_ORDER,
              OrderItemRequestType.SYSTEM_AMENDMENT_ORDER,
            ].includes(type);

          //checking creation order type with selected entitlement detail id.
          const isValidCreationOrder =
            type === OrderItemRequestType.CREATION_ORDER && orderEntitlementId === entitlementDetails.id;

          return isValidOrderType || isValidCreationOrder;
        }) ?? [];
      let startDate = amendmentOrders[amendmentOrders.length - 1]?.created ?? null;
      for (let i = amendmentOrders.length - 1; i >= 0; i--) {
        const currentOrder = amendmentOrders[i]?.orderLineItems?.nodes[0];
        if (i > 0) {
          const nextOrder = amendmentOrders[i - 1]?.orderLineItems?.nodes[0];
          if (!(currentOrder && nextOrder)) continue;
          //checking if offering is changed
          const isOfferingIsChanged = currentOrder?.offeringId !== nextOrder?.offeringId;

          //checking if pricing plan is changed
          const isPricingPlanChanged = !currentOrder?.pricingPlan && nextOrder?.pricingPlan;

          //checking if billing frequency is changed to anual
          const isBillingFrequencyChangedToAnnual =
            currentOrder?.pricingPlan?.billingFrequency !== nextOrder?.pricingPlan?.billingFrequency &&
            nextOrder?.pricingPlan?.billingFrequency === BillingFrequency.ANNUAL;

          if (isOfferingIsChanged || isPricingPlanChanged || isBillingFrequencyChangedToAnnual) {
            startDate = amendmentOrders[i - 1]?.created ?? null;
          }
        }
      }
      return startDate;
    },
    []
  );

  const getSubscriptionStartDate = React.useCallback(
    async (entitlementId: string, billingStartDate?: string): Promise<string | null> => {
      try {
        setLoading(true);
        const { data, error } = await client.query<Query>({
          query: fetchSubscriptionOrderHistory,
          variables: {
            id: entitlementId,
            searchOptions: {
              first: 1000,
            },
          },
        });
        if (error) {
          throw error;
        }
        return calculateSubscriptionStartDate(data.entitlementDetails, billingStartDate);
      } catch (e) {
        setError(e as Error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [calculateSubscriptionStartDate, client]
  );

  return {
    getSubscriptionStartDate,
    error,
    loading,
  };
};
